import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { convertDataForSortableList } from 'graphql/utils'
import { CreateTag, TagType } from 'generated/graphql'
import _ from 'lodash'
import TagRow, { Tag } from '../TagRow'
import SortableList, { SortableItem } from './SortableList'
import MoreOptionsButton from '../MoreOptionsButton'

export interface Category {
    id: number
    text: string
    order: number
    tags: Tag[]
}

interface Props {
    categories: Category[]
    unassignedTags: TagType[]
    fetchTags: () => Promise<void>
    updateMinCategoryOrder?: (order: number) => void
    updateMinTagOrder?: (order: number) => void
    fetchCategories: () => Promise<void>
    updateTagOrder: (id, order, categoryId?) => Promise<any>
    updateCategoryOrder: (id, order) => Promise<any>
    deleteCategory: (id) => Promise<any>
    deleteTag: (id) => Promise<any>
    updateTag:({ text, color, categoryId }, id) => Promise<any>
    updateCategory: (id, text) => Promise<any>
    createTag:(data: CreateTag) => Promise<any>
}

const S = {
    CategoryWrapper: styled.div`
        width: 100%;
    `,
    Row: styled.div`
        width: 100%;
        margin-bottom: 30px;
    `,
    CategoryHeader: styled.div`
        display: flex;
        background: none;
        width: 100%;
        justify-content: space-between;
    `,
    CategoryBody: styled.div`
    `,
    CategoryTitle: styled.div`
        font-size: 16px;
        text-transform: uppercase;
        font-weight: 700;
        color: #78909C;
        opacity: 0.75;
    `
}

interface CategoryRowProps {
    tags: Tag[] | TagType[]
    categories: Category[]
    unassignedTags: Tag[]
    categoryId?: number
    fetchData: () => Promise<void>
    updateTagOrder: (id, order, categoryId?) => Promise<any>
    updateMinTagOrder: (order: number) => void
    deleteTag:(id) => Promise<any>
    updateTag:({ text, color, categoryId }, id) => Promise<any>
}

const CategoryList = ({
    categories, unassignedTags, fetchTags, updateMinCategoryOrder, fetchCategories,
    updateTagOrder, updateCategoryOrder, updateMinTagOrder, deleteCategory, deleteTag, updateTag,
    updateCategory, createTag
}: Props) => {
    const [items, setItems] = useState<SortableItem[]>([])
    const renderItem = (category) => (
        <S.CategoryHeader key={category.text}>
            <S.CategoryTitle>{category.text}</S.CategoryTitle>
            <MoreOptionsButton
                category={category}
                fetchData={async () => { await fetchTags(); await fetchCategories() }}
                deleteCategory={deleteCategory}
                createTag={createTag}
                updateCategory={updateCategory}
            />
        </S.CategoryHeader>
    )
    const renderBody = (category: Category) => (
        <CategoryRow
            deleteTag={deleteTag}
            unassignedTags={unassignedTags}
            categoryId={category.id}
            key={category.text}
            categories={categories}
            tags={category.tags}
            fetchData={fetchTags}
            updateTagOrder={updateTagOrder}
            updateMinTagOrder={updateMinTagOrder}
            updateTag={updateTag}
        />
    )
    useEffect(() => {
        if (categories) {
            const dataForSortableList = convertDataForSortableList(
                categories, renderItem, 'order', renderBody
            )
            setItems(dataForSortableList)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories])
    return (
        <S.CategoryWrapper>
            <CategoryRow
                unassignedTags={unassignedTags}
                categories={categories}
                tags={unassignedTags}
                fetchData={fetchTags}
                updateTagOrder={updateTagOrder}
                updateMinTagOrder={updateMinTagOrder}
                deleteTag={deleteTag}
                updateTag={updateTag}
            />
            <SortableList
                initialList={items}
                updateOrder={updateCategoryOrder}
                onUpdateMinOrder={updateMinCategoryOrder}
                styledBackground={false}
            />
        </S.CategoryWrapper>
    )
}

const CategoryRow = ({
    tags, fetchData, updateTagOrder, categories, unassignedTags, categoryId, updateMinTagOrder,
    deleteTag, updateTag
}: CategoryRowProps) => {
    const [items, setItems] = useState<SortableItem[]>([])
    useEffect(() => {
        if (tags) {
            const dataForSortableList = convertDataForSortableList(
                tags, renderItem
            )
            setItems(dataForSortableList)
        } else {
            setItems([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tags])
    const renderItem = (tag) => (
        <TagRow
            key={tag.text}
            tag={tag}
            fetchData={fetchData}
            deleteRow={deleteTag}
            updateTag={updateTag}
        />
    )

    return (
        <S.Row>
            <SortableList
                initialList={items}
                groups={_.flatMap(categories, 'tags').concat(unassignedTags)}
                listId={categoryId}
                updateOrder={updateTagOrder}
                fetchData={fetchData}
                onUpdateMinOrder={updateMinTagOrder}
                group="categoryRow"
            />
        </S.Row>
    )
}

export default CategoryList
